// Qué muestra la pantalla de Herramientas del teléfono y en qué orden
// (ADR-2026-10-09-herramientas-en-el-telefono-a-pantalla-completa): las cuatro principales
// arriba, a todo el ancho, y el resto de a dos por fila. Cada una con su imagen —las
// carátulas de cada modo y de cada atajo— y una explicación más larga que la bajada de una
// línea de la lista.
//
// Puro y sin JSX: es lo que se testea. Las herramientas llegan resueltas de
// `useModosGenerar` (candado por plan, a dónde navega cada una): acá sólo se ordenan y se
// visten.

import type { ComponentType } from 'react';
import type { EdicionSheet, ModoSheet } from './SheetGenerar';
import { bajadaCortaModo } from '../modesCatalog';
import { palabraDeModeloActual } from '../../../lib/nombreDeModelo';

/** Lo que se ve arriba de la card. */
export type MedioDeHerramienta =
  /** Una carátula. `contain` para las ilustraciones con fondo transparente (van sobre el
   *  tinte de la card); `cover` para las fotos. */
  | { tipo: 'imagen'; src: string; ajuste: 'cover' | 'contain' }
  /** Un clip en loop, con su póster mientras carga. */
  | { tipo: 'video'; src: string; poster: string }
  /** Una carátula dibujada (el Estudio de Video no tiene foto). */
  | { tipo: 'dibujo'; Dibujo: ComponentType };

export interface HerramientaMobile {
  /** El `label` de la edición o el `path` del modo. */
  id: string;
  nombre: string;
  explicacion: string;
  medio: MedioDeHerramienta;
  /** El fondo de la card (liso, como las cards por destino de la home). */
  tinte: string;
  bloqueado: boolean;
  /** Se lista, pero se abre en la computadora. */
  soloEnComputadora: boolean;
  onAbrir: () => void;
}

/**
 * Las cuatro que van arriba, a todo el ancho, en este orden. Son ids de los dos mundos: el
 * Estudio es un modo (`editor-video`) y las otras tres son atajos de ediciones rápidas.
 */
export const HERRAMIENTAS_PRINCIPALES = ['editor-video', 'Animar', 'Carrusel', 'Editar'] as const;

/** Herramientas que se listan pero no se abren en el teléfono (su pantalla dice
 *  "Disponible en computadora"). La misma regla que la lista de `SheetPorDestino`. */
export const SOLO_EN_COMPUTADORA: ReadonlySet<string> = new Set(['diseno-indumentaria']);

/** El tinte de la card, como los de las cards por destino de la home: liso y suave. */
const LAVANDA = '#EEF0F8';

/**
 * Nombre (si cambia), explicación, medio y tinte de cada herramienta, por id. Todas son
 * cortas: en las principales el texto va en la mitad izquierda de la card (la imagen, a la
 * derecha) y en el resto, en media columna; tienen que entrar en tres o cuatro líneas. Las de los modos siguen a lo
 * que el agente sabe de ellos (`QUE_HACE_EL_MODO`).
 */
const ARTE: Record<
  string,
  { nombre?: string; explicacion: string | (() => string); medio?: MedioDeHerramienta; tinte?: string }
> = {
  'editor-video': {
    explicacion: 'Cortá, ordená escenas y sumá voces con IA en una línea de tiempo.',
    tinte: '#EAEDF7',
  },
  Animar: {
    nombre: 'Animar imagen',
    explicacion: 'Convertí una foto en un video corto: contás qué querés que pase y la IA la anima.',
    tinte: '#F2ECF5',
    medio: {
      tipo: 'video',
      src: '/images/modo_animar_imagen/caratula.mp4',
      poster: '/images/modo_animar_imagen/caratula_poster.jpg',
    },
  },
  Carrusel: {
    explicacion: 'De una foto, nuevas tomas del mismo producto: otros ángulos y encuadres.',
    tinte: '#F4F1E6',
    medio: { tipo: 'imagen', src: '/images/modo_carrusel/caratula_card.webp', ajuste: 'cover' },
  },
  Editar: {
    explicacion: 'Pedile un cambio con tus palabras —el fondo, la luz, un objeto— y listo.',
    medio: { tipo: 'imagen', src: '/images/editar_prompt_card.webp', ajuste: 'contain' },
    tinte: '#EDF0FA',
  },
  Descripción: {
    explicacion: 'Subí una foto y te escribimos el texto del post, con hashtags.',
    medio: { tipo: 'imagen', src: '/images/modo_descripcion/caratula_card.webp', ajuste: 'contain' },
    tinte: '#EEF2FB',
  },
  Recortar: {
    explicacion: 'Ajustá el encuadre y llevá la foto al formato que necesites.',
    medio: { tipo: 'imagen', src: '/images/modo_recortar/caratula_card.webp', ajuste: 'contain' },
    tinte: '#F5F0E6',
  },
  Expandir: {
    explicacion: 'La IA completa los costados para llevar la foto a otro formato.',
    medio: { tipo: 'imagen', src: '/images/modo_expandir/caratula_card.webp', ajuste: 'contain' },
    tinte: '#FBEFEA',
  },
  Mejorar: {
    explicacion: 'Subí la resolución con IA para que se vea nítida en grande.',
    medio: { tipo: 'imagen', src: '/images/modo_mejorar/caratula_card.webp', ajuste: 'contain' },
    tinte: '#E9F4EF',
  },
  'replicar-foto': {
    explicacion: () =>
      `Una foto que te guste, rehecha con tus productos y tu ${palabraDeModeloActual().singular.toLowerCase()}.`,
    tinte: '#F2ECF5',
  },
  'diseno-indumentaria': {
    explicacion: 'Para diseñadores: del plano técnico a la prenda puesta.',
    tinte: '#F4F1E6',
  },
  flujos: {
    explicacion: 'Encadená pasos de la foto al video y repetilo con otro producto.',
    tinte: '#EAEDF7',
  },
};

const deEdicion = (e: EdicionSheet): HerramientaMobile => {
  const arte = ARTE[e.label];
  const explicacion = arte ? (typeof arte.explicacion === 'function' ? arte.explicacion() : arte.explicacion) : e.desc ?? '';
  return {
    id: e.label,
    nombre: arte?.nombre ?? e.label,
    explicacion,
    // Un atajo sin carátula (uno nuevo que todavía no está en `ARTE`) no se cae: va con el
    // tinte solo y su nombre.
    medio: arte?.medio ?? { tipo: 'imagen', src: '', ajuste: 'contain' },
    tinte: arte?.tinte ?? LAVANDA,
    bloqueado: !!e.bloqueado,
    soloEnComputadora: false,
    onAbrir: e.onAbrir,
  };
};

const deModo = (m: ModoSheet): HerramientaMobile => {
  const arte = ARTE[m.path];
  const explicacion = arte
    ? typeof arte.explicacion === 'function'
      ? arte.explicacion()
      : arte.explicacion
    : bajadaCortaModo(m.path, '');
  return {
    id: m.path,
    nombre: arte?.nombre ?? m.titulo,
    explicacion,
    medio:
      arte?.medio ??
      (m.Caratula
        ? { tipo: 'dibujo', Dibujo: m.Caratula }
        : { tipo: 'imagen', src: m.imagen, ajuste: 'cover' }),
    tinte: arte?.tinte ?? LAVANDA,
    bloqueado: m.bloqueado,
    soloEnComputadora: SOLO_EN_COMPUTADORA.has(m.path),
    onAbrir: m.onAbrir,
  };
};

/**
 * Las herramientas de la pantalla: las principales (las que esta cuenta ve, en el orden de
 * `HERRAMIENTAS_PRINCIPALES`) y el resto, primero los atajos y después los modos, como en la
 * lista de siempre. Las que piden computadora van al final: no se pueden abrir acá.
 */
export function herramientasDeLaPantalla(
  ediciones: EdicionSheet[],
  modos: ModoSheet[],
): { principales: HerramientaMobile[]; resto: HerramientaMobile[] } {
  const todas = [...ediciones.map(deEdicion), ...modos.map(deModo)];
  const esPrincipal = (id: string) => (HERRAMIENTAS_PRINCIPALES as readonly string[]).includes(id);
  const principales = HERRAMIENTAS_PRINCIPALES.flatMap((id) => todas.filter((h) => h.id === id));
  const resto = todas.filter((h) => !esPrincipal(h.id));
  return {
    principales,
    resto: [...resto.filter((h) => !h.soloEnComputadora), ...resto.filter((h) => h.soloEnComputadora)],
  };
}
